'use client'

import React from 'react'
import { ArrowUpRight } from 'lucide-react'
import InfiniteScroll from 'react-infinite-scroll-component'
import useWindowSize from '@/utils/hooks/useWindowSize'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow
} from '@/components/ui/table'
import Loading from '@/components/circle-loading'
import formatNumber from '@/utils/format-number'
import getRelativeTime from '@/utils/get-relative-time'
import useNatOwners, { NatOwnerRow, useNatOwnersInfinite } from '@/utils/hooks/useNatOwners'

const PAGE_SIZE = 50
const BURN_ADDRESSES = new Set(['1bitcoineateraddressdontsendf59kue'])

const fmt = (n: number | undefined | null): string => {
	if (n == null || !isFinite(n)) return '—'
	if (n === 0) return '0'
	const s = formatNumber(n)
	return typeof s === 'string' ? s : String(n)
}

const pct = (n: number | null | undefined): string => {
	if (n == null || !isFinite(n)) return '—'
	if (n === 0) return '0%'
	if (n < 0.01) return '<0.01%'
	return `${n.toFixed(2)}%`
}

type Tone = 'whale' | 'shark' | 'fish' | 'minnow' | 'miner' | 'locked' | 'burn'

function Badge({ label, tone }: { label: string; tone: Tone }) {
	const toneClass =
		tone === 'whale'
			? 'border-purple-500/20 bg-purple-500/10 text-purple-700 dark:text-purple-300'
			: tone === 'shark'
			? 'border-sky-500/20 bg-sky-500/10 text-sky-700 dark:text-sky-300'
			: tone === 'fish'
			? 'border-teal-500/20 bg-teal-500/10 text-teal-700 dark:text-teal-300'
			: tone === 'minnow'
			? 'border-border bg-accent text-foreground/55'
			: tone === 'miner'
			? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
			: tone === 'burn'
			? 'border-red-500/20 bg-red-500/20 text-red-700 dark:text-red-300'
			: 'border-blue-500/20 bg-blue-500/20 text-blue-700 dark:text-blue-300'
	return (
		<span
			className={`inline-flex items-center justify-center max-w-full whitespace-nowrap rounded-md border px-1.5 py-1 text-[9px] font-semibold leading-none ${toneClass}`}
		>
			{label}
		</span>
	)
}

/**
 * Primary identity tag. For miners we show the pool name (or plain "Miner"
 * if unknown) and skip the size tier entirely — the pool identity is more
 * useful than "Whale" when you already know the address is a mining pool.
 * Otherwise we classify by share of circulating supply:
 *   Whale  ≥ 0.5%
 *   Shark  ≥ 0.1%
 *   Fish   ≥ 0.01%
 *   Minnow < 0.01%
 */
function primaryTag(row: NatOwnerRow): { label: string; tone: Tone } {
	if (BURN_ADDRESSES.has(row.address.toLowerCase())) {
		return { label: 'Burn', tone: 'burn' }
	}
	if (row.isMiner) {
		return { label: row.minerLabel || 'Miner', tone: 'miner' }
	}
	if (row.isWhale) return { label: 'Whale', tone: 'whale' }
	const p = row.percent ?? 0
	if (p >= 0.1) return { label: 'Shark', tone: 'shark' }
	if (p >= 0.01) return { label: 'Fish', tone: 'fish' }
	return { label: 'Minnow', tone: 'minnow' }
}

function StatusCell({ row }: { row: NatOwnerRow }) {
	const badges: Array<{ label: string; tone: Tone }> = [primaryTag(row)]
	if (row.balance > 0 && row.locked != null) {
		const lockedPct = row.locked / row.balance
		if (lockedPct >= 0.99) badges.push({ label: 'Locked', tone: 'locked' })
		else if (lockedPct > 0.01)
			badges.push({ label: `${Math.round(lockedPct * 100)}% locked`, tone: 'locked' })
	}
	return (
		<div className="flex flex-wrap justify-end gap-1">
			{badges.map((b, i) => (
				<Badge key={i} label={b.label} tone={b.tone} />
			))}
		</div>
	)
}

interface NatOwnersProps {
	height?: string
	hideHeader?: boolean
	compact?: boolean
	ticker?: string
}

export function NatOwners({
	height,
	hideHeader = false,
	compact = false,
	ticker = 'dmt-nat'
}: NatOwnersProps) {
	const { isMobile } = useWindowSize()
	const { holders, meta, hasMore, error, isLoading, loadMore, retry } = useNatOwnersInfinite(
		PAGE_SIZE,
		ticker
	)

	const primaryBlocked =
		meta?.sources?.tapalytics === 'blocked' &&
		meta?.sources?.natgmi !== 'ok' &&
		meta?.sources?.trac !== 'ok'

	const scrollId = compact ? 'nat-owners-scrollable-compact' : 'nat-owners-scrollable'

	return (
		<div
			className="flex h-full min-h-0 flex-col"
			style={isMobile || !height ? undefined : { height }}
		>
			{!hideHeader && (
				<div className="px-4 py-3 md:py-4">
					<h1 className="text-md font-semibold">Alpha</h1>
				</div>
			)}

			<div className="border-b border-border px-3 py-3 text-xs">
				<div className="flex items-center justify-between gap-2">
					<h2 className="font-semibold">Rich list</h2>
					<a
						className="inline-flex items-center gap-1 text-muted-foreground no-underline transition-colors hover:text-foreground"
						href={`https://www.tapalytics.xyz/token/${encodeURIComponent(ticker)}`}
						target="_blank"
						rel="noopener noreferrer"
					>
						Tapalytics <ArrowUpRight aria-hidden="true" className="size-3.5" strokeWidth={1.7} />
					</a>
				</div>
				{meta && (
					<p className="mt-1 block text-[10px] text-muted-foreground">
						{meta.totalHolders.toLocaleString()} holders · Retrieved{' '}
						{new Date(meta.updatedAt).toLocaleTimeString([], {
							hour: '2-digit',
							minute: '2-digit'
						})}
					</p>
				)}
				<p className="mt-1 block text-[10px] text-muted-foreground">
					Ranked by balance. Supply share uses circulating supply.
				</p>
			</div>

			{primaryBlocked && (
				<div className="mx-4 mt-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-300">
					Live holder data from tapalytics / Trac upstreams is temporarily unavailable.
				</div>
			)}

			<div className="flex flex-col">
				<Table className="w-full table-fixed">
					<TableHeader>
						<TableRow>
							<TableHead className="w-[10%] pl-2 pr-0 text-left text-[10px] text-muted-foreground whitespace-nowrap">
								#
							</TableHead>
							<TableHead className="w-[22%] pl-2 pr-0 text-left text-[10px] text-muted-foreground whitespace-nowrap">
								Address
							</TableHead>
							<TableHead className="w-[22%] pl-0 text-left text-[10px] text-muted-foreground whitespace-nowrap">
								Balance
							</TableHead>
							<TableHead className="w-[18%] pl-0 text-left text-[10px] text-muted-foreground whitespace-nowrap">
								%
							</TableHead>
							<TableHead className="w-[28%] pr-2 text-right text-[10px] text-muted-foreground whitespace-nowrap">
								Status
							</TableHead>
						</TableRow>
					</TableHeader>
				</Table>
			</div>

			<div id={scrollId} className={isMobile ? '' : 'min-h-0 flex-1 overflow-y-auto'}>
				<div className="min-w-full">
					{isLoading && holders.length === 0 ? (
						<div className="m-4 flex h-full items-center justify-center">
							<Loading />
						</div>
					) : error && holders.length === 0 ? (
						<div className="flex h-full items-center justify-center p-4 text-red-500">
							<span>
								Rich list temporarily unavailable.{' '}
								<button type="button" className="underline" onClick={() => retry()}>
									Retry
								</button>
							</span>
						</div>
					) : holders.length === 0 ? (
						<div className="flex h-full items-center justify-center p-4 text-muted-foreground">
							No holders reported by Tapalytics.
						</div>
					) : (
						<InfiniteScroll
							dataLength={holders.length}
							next={loadMore}
							hasMore={hasMore}
							loader={
								<div className="flex justify-center py-3">
									<Loading size={16} />
								</div>
							}
							{...(isMobile ? {} : { scrollableTarget: scrollId })}
						>
							<Table className="w-full table-fixed">
								<TableBody>
									{holders.map((row) => (
										<TableRow key={row.address} className="whitespace-nowrap border-0">
											<TableCell className="w-[10%] pl-2 pr-0 py-1 text-left text-xs text-muted-foreground">
												{row.rank}
											</TableCell>
											<TableCell className="w-[22%] pl-2 pr-0 py-2 text-left text-xs text-foreground">
												<a
													href={`/address/${row.address}`}
													className="hover:text-purple-700 dark:hover:text-purple-300 hover:underline"
													onClick={(e) => e.stopPropagation()}
													title={row.address}
												>
													{row.address.slice(-5)}
												</a>
											</TableCell>
											<TableCell className="w-[22%] pl-0 py-1 text-left text-xs text-foreground">
												<span
													title={row.balance.toLocaleString('en-US', { maximumFractionDigits: 8 })}
												>
													{fmt(row.balance)}
												</span>
											</TableCell>
											<TableCell className="w-[18%] pl-0 py-1 text-left text-xs text-foreground/70 whitespace-nowrap">
												{pct(row.percent)}
											</TableCell>
											<TableCell className="w-[28%] pr-2 py-1 text-right">
												<div className="flex items-center justify-end gap-1">
													{row.lastMovedAt ? (
														<span className="text-[10px] text-muted-foreground">
															{getRelativeTime(row.lastMovedAt)}
														</span>
													) : null}
													<StatusCell row={row} />
												</div>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</InfiniteScroll>
					)}
					{error && holders.length > 0 && (
						<button type="button" onClick={() => retry()} className="w-full p-3 text-xs underline">
							Could not load more holders. Retry
						</button>
					)}
				</div>
			</div>
		</div>
	)
}

/**
 * Compact preview card listing top-N holders. Used inside NatInsights.
 */
export function NatOwnersPreview({
	onViewAll,
	limit = 5,
	ticker = 'dmt-nat'
}: {
	onViewAll?: () => void
	limit?: number
	ticker?: string
}) {
	const { data, isLoading, error } = useNatOwners(true, Math.max(limit, 5), ticker)
	const rows = (data?.holders || []).slice(0, limit)

	return (
		<div className="mx-5 mb-3 rounded-xl bg-muted p-4">
			<div className="mb-3 flex items-center justify-between">
				<div className="text-[10px] font-semibold text-foreground/50">
					Top Holders
				</div>
				{onViewAll && (
					<button
						type="button"
						onClick={onViewAll}
						className="text-[10px] font-semibold text-muted-foreground hover:text-foreground"
					>
						View all →
					</button>
				)}
			</div>

			{isLoading && !data ? (
				<div className="flex items-center justify-center py-4">
					<Loading size={16} />
				</div>
			) : rows.length === 0 ? (
				<div className="py-3 text-center text-xs text-muted-foreground">
					{error ? 'Rich list temporarily unavailable.' : 'No holders reported.'}
				</div>
			) : (
				<div className="flex flex-col gap-2">
					{rows.map((row) => {
						const tag = primaryTag(row)
						return (
							<button
								key={row.address}
								type="button"
								onClick={onViewAll}
								className="flex w-full items-center justify-between gap-2 rounded-md p-1 text-left hover:bg-foreground/5"
							>
								<div className="flex min-w-0 items-center gap-2">
									<span className="w-5 shrink-0 text-[11px] text-muted-foreground">
										#{row.rank}
									</span>
									<a
										href={`/address/${row.address}`}
										className="truncate text-xs text-foreground hover:text-purple-700 dark:hover:text-purple-300"
										onClick={(e) => e.stopPropagation()}
										title={row.address}
									>
										{row.address.slice(-5)}
									</a>
									<Badge label={tag.label} tone={tag.tone} />
								</div>
								<div className="flex shrink-0 flex-col items-end">
									<span className="text-xs font-semibold text-foreground">
										<span title={row.balance.toLocaleString('en-US', { maximumFractionDigits: 8 })}>
											{fmt(row.balance)}
										</span>
									</span>
									<span className="text-[10px] text-muted-foreground">{pct(row.percent)}</span>
								</div>
							</button>
						)
					})}
				</div>
			)}
		</div>
	)
}
